/**
 * API client for Account Portal
 */

export const API_BASE =
  import.meta.env.VITE_API_BASE ||
  (import.meta.env.DEV ? 'http://localhost:3000/api' : '/api');

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function fetchApi(endpoint: string, options: RequestInit = {}) {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
    credentials: 'include',
    cache: options.cache ?? 'no-store',
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new ApiError(
      data.message || data.error || `HTTP ${response.status}`,
      response.status,
      data
    );
  }

  if (response.status === 204) return null;
  const contentType = response.headers.get('Content-Type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }
  return response.text();
}

export const api = {
  // Authentication
  auth: {
    signIn: (email: string, password: string) =>
      fetchApi('/auth/sign-in/email', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }),

    signUp: (email: string, password: string, name?: string) => {
      const portalUrl =
        import.meta.env.VITE_PORTAL_URL || window.location.origin;
      const callbackURL = `${portalUrl}/login?verified=1`;
      return fetchApi('/auth/sign-up/email', {
        method: 'POST',
        body: JSON.stringify({ email, password, name, callbackURL }),
      });
    },

    forgotPassword: (email: string) => {
      const redirectTo =
        import.meta.env.VITE_PORTAL_URL || window.location.origin;
      return fetchApi('/auth/request-password-reset', {
        method: 'POST',
        body: JSON.stringify({ email, redirectTo: `${redirectTo}/reset-password` }),
      });
    },
    resetPassword: (token: string, newPassword: string) =>
      fetchApi('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, newPassword }),
      }),

    sendVerificationEmail: (email: string) => {
      const portalUrl =
        import.meta.env.VITE_PORTAL_URL || window.location.origin;
      const callbackURL = `${portalUrl}/login?verified=1`;
      return fetchApi('/auth/send-verification-email', {
        method: 'POST',
        body: JSON.stringify({ email, callbackURL }),
      });
    },

    changePassword: (currentPassword: string, newPassword: string) =>
      fetchApi('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      }),

    signOut: () =>
      fetchApi('/auth/sign-out', {
        method: 'POST',
      }),

    getSession: async () => {
      const data = await fetchApi('/auth/get-session');
      if (data && typeof data === 'object' && 'user' in (data as Record<string, unknown>)) {
        return (data as Record<string, unknown>).user;
      }
      return data;
    },
  },

  // Organizations
  organizations: {
    list: () => fetchApi('/organizations'),

    get: (id: string) => fetchApi(`/organizations/${id}`),

    create: (data: { name: string; description?: string }) =>
      fetchApi('/organizations', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: { name: string; description?: string }) =>
      fetchApi(`/organizations/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      fetchApi(`/organizations/${id}`, {
        method: 'DELETE',
      }),

    members: (id: string) => fetchApi(`/organizations/${id}/members`),

    removeMember: (orgId: string, userId: string) =>
      fetchApi(`/organizations/${orgId}/members/${userId}`, {
        method: 'DELETE',
      }),

    updateMemberRole: (orgId: string, userId: string, role: string) =>
      fetchApi(`/organizations/${orgId}/members/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      }),

    promoteMember: (orgId: string, userId: string) =>
      fetchApi(`/organizations/${orgId}/members/${userId}/promote`, {
        method: 'POST',
      }),

    demoteMember: (orgId: string, userId: string) =>
      fetchApi(`/organizations/${orgId}/members/${userId}/demote`, {
        method: 'POST',
      }),
  },

  // Invitations (all invitations start as MEMBER role)
  invitations: {
    create: (orgId: string, email: string) =>
      fetchApi(`/organizations/${orgId}/invitations`, {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),

    list: (orgId: string) => fetchApi(`/organizations/${orgId}/invitations`),

    revoke: (orgId: string, inviteId: string) =>
      fetchApi(`/organizations/${orgId}/invitations/${inviteId}`, {
        method: 'DELETE',
      }),

    get: (token: string) => fetchApi(`/invitations/${token}`),

    accept: (token: string) =>
      fetchApi(`/invitations/${token}/accept`, {
        method: 'POST',
      }),

    decline: (token: string) =>
      fetchApi(`/invitations/${token}/decline`, {
        method: 'POST',
      }),

    acceptPublic: (token: string) =>
      fetchApi(`/invitations/${token}/accept-public`, {
        method: 'POST',
      }),

    declinePublic: (token: string) =>
      fetchApi(`/invitations/${token}/decline-public`, {
        method: 'POST',
      }),
  },

  // Apps
  apps: {
    requestAccess: (appId: string) => fetchApi(`/apps/${encodeURIComponent(appId)}/access-requests`, { method: 'POST' }),
    list: () => fetchApi('/apps'),
    config: () => fetchApi('/apps/config'),

    get: (id: string) => fetchApi(`/apps/${id}`),
  },

  // Subscriptions (TODO: implement backend endpoints)
  subscriptions: {
    listUser: () => fetchApi('/subscriptions/user'),

    listOrganization: (orgId: string) =>
      fetchApi(`/subscriptions/organization/${orgId}`),

    checkout: (
      appId: string,
      priceId: string,
      organizationId?: string,
      quantity?: number
    ) =>
      fetchApi('/subscriptions/checkout', {
        method: 'POST',
        body: JSON.stringify({ appId, priceId, organizationId, quantity }),
      }),

    startTrial: (appId: string, organizationId?: string, quantity?: number) =>
      fetchApi('/subscriptions/trial', {
        method: 'POST',
        body: JSON.stringify({ appId, organizationId, quantity }),
      }),

    cancel: (id: string) =>
      fetchApi(`/subscriptions/${id}/cancel`, {
        method: 'POST',
      }),

    portal: (organizationId?: string) =>
      fetchApi('/subscriptions/portal', {
        method: 'POST',
        body: JSON.stringify({ organizationId }),
      }),
  },

  // Seat Management
  seats: {
    // Assign user to product
    assign: (orgId: string, userId: string, appId: string) =>
      fetchApi(`/organizations/${orgId}/seats`, {
        method: 'POST',
        body: JSON.stringify({ userId, appId }),
      }),

    // Unassign user from product
    unassign: (orgId: string, userId: string, appId: string) =>
      fetchApi(`/organizations/${orgId}/seats/${userId}/${appId}`, {
        method: 'DELETE',
      }),

    // List all seat assignments for organization
    listOrganization: (orgId: string) =>
      fetchApi(`/organizations/${orgId}/seats`),

    // Get available seats for a product
    getAvailable: (orgId: string, appId: string) =>
      fetchApi(`/organizations/${orgId}/seats/available/${appId}`),
  },

  // User product assignments
  user: {
    me: () => fetchApi('/users/me'),
    ensureTrials: () =>
      fetchApi('/users/me/ensure-trials', {
        method: 'POST',
      }),
    accountDeletionStatus: () => fetchApi('/users/me/account-deletion-status'),
    deleteAccount: () =>
      fetchApi('/users/me', {
        method: 'DELETE',
      }),
    products: () => fetchApi('/users/me/products'),

    checkAccess: (appId: string) =>
      fetchApi(`/users/me/products/${appId}/access`),

    favorites: () => fetchApi('/users/me/favorites'),

    addFavorite: (appId: string) =>
      fetchApi('/users/me/favorites', {
        method: 'POST',
        body: JSON.stringify({ appId }),
      }),

    removeFavorite: (appId: string) =>
      fetchApi(`/users/me/favorites/${appId}`, {
        method: 'DELETE',
      }),
  },

  // Admin (super-admin only)
  admin: {
    getTerrainOverview: (days: number) => fetchApi(`/admin/terrain?days=${days}`),
    listAnnouncements: (options?: { page?: number; pageSize?: number; search?: string; type?: string; status?: string }) => {
      const params = new URLSearchParams();
      Object.entries(options || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== '') params.set(key, String(value));
      });
      const query = params.toString();
      return fetchApi(`/admin/announcements${query ? `?${query}` : ''}`);
    },
    getAnnouncement: (id: string) => fetchApi(`/admin/announcements/${id}`),
    createAnnouncement: (data: Record<string, unknown>) =>
      fetchApi('/admin/announcements', { method: 'POST', body: JSON.stringify(data) }),
    updateAnnouncement: (id: string, data: Record<string, unknown>) =>
      fetchApi(`/admin/announcements/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    deleteAnnouncement: (id: string) =>
      fetchApi(`/admin/announcements/${id}`, { method: 'DELETE' }),
    copyAnnouncement: (id: string) =>
      fetchApi(`/admin/announcements/${id}/copy`, { method: 'POST' }),
    resetAnnouncementViews: (id: string) =>
      fetchApi(`/admin/announcements/${id}/reset-views`, { method: 'POST' }),
    previewAnnouncement: (data: Record<string, unknown>) =>
      fetchApi('/admin/announcements/preview', { method: 'POST', body: JSON.stringify(data) }),

    listApps: () => fetchApi('/admin/apps'),
    exportAppsConfiguration: () => fetchApi('/admin/export-import/apps'),
    importAppsConfiguration: (configuration: unknown) => fetchApi('/admin/export-import/apps', {
      method: 'POST',
      body: JSON.stringify(configuration),
    }),

    getConfig: () => fetchApi('/admin/config'),

    createApp: (data: {
      name: string;
      slug: string;
      description: string;
      link: string;
      badge?: string | null;
      sortOrder?: number;
      isActive?: boolean;
      isAnneaOnly?: boolean;
      terrainEnabled?: boolean;
      stripeProductId?: string | null;
      parentId?: string | null;
      isFree?: boolean;
      isAdminGranted?: boolean;
      isCatalogGroup?: boolean;
      catalogVisibility?: 'PUBLIC' | 'ACCESS_ONLY';
      iconColor?: string | null;
      infoUrl?: string | null;
    }) =>
      fetchApi('/admin/apps', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateApp: (id: string, data: Partial<{
      name: string;
      slug: string;
      description: string;
      link: string;
      badge?: string | null;
      sortOrder?: number;
      isActive?: boolean;
      isAnneaOnly?: boolean;
      terrainEnabled?: boolean;
      stripeProductId?: string | null;
      parentId?: string | null;
      isFree?: boolean;
      isAdminGranted?: boolean;
      isCatalogGroup?: boolean;
      catalogVisibility?: 'PUBLIC' | 'ACCESS_ONLY';
      iconColor?: string | null;
      infoUrl?: string | null;
    }>) =>
      fetchApi(`/admin/apps/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    deleteApp: (id: string) =>
      fetchApi(`/admin/apps/${id}`, {
        method: 'DELETE',
      }),

    createPrice: (appId: string, data: {
      stripePriceId: string;
      interval: 'MONTH' | 'YEAR' | 'CUSTOM';
      name?: string | null;
      currency?: string;
      unitAmount?: number | null;
      isActive?: boolean;
      metadata?: Record<string, unknown> | null;
    }) =>
      fetchApi(`/admin/apps/${appId}/prices`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updatePrice: (id: string, data: Partial<{
      stripePriceId: string;
      interval: 'MONTH' | 'YEAR' | 'CUSTOM';
      name?: string | null;
      currency?: string;
      unitAmount?: number | null;
      isActive?: boolean;
      metadata?: Record<string, unknown> | null;
    }>) =>
      fetchApi(`/admin/prices/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    deletePrice: (id: string) =>
      fetchApi(`/admin/prices/${id}`, {
        method: 'DELETE',
      }),

    listOrganizations: () => fetchApi('/admin/organizations'),

    getOrganization: (id: string) => fetchApi(`/admin/organizations/${id}`),

    createOrganization: (data: {
      name: string;
      description?: string;
      slug?: string;
      manualInvoicing?: boolean;
    }) =>
      fetchApi('/admin/organizations', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateOrganization: (id: string, data: { manualInvoicing: boolean }) =>
      fetchApi(`/admin/organizations/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    deleteOrganization: (id: string) =>
      fetchApi(`/admin/organizations/${id}`, {
        method: 'DELETE',
      }),

    createOrganizationSubscription: (
      orgId: string,
      data: { appId: string; priceId?: string; quantity?: number; trialDays?: number; trialOnly?: boolean; manual?: boolean; expiresAt?: string | null }
    ) =>
      fetchApi(`/admin/organizations/${orgId}/subscriptions`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    updateOrganizationSubscriptionSeats: (
      orgId: string,
      subscriptionId: string,
      seatCount: number
    ) =>
      fetchApi(`/admin/organizations/${orgId}/subscriptions/${subscriptionId}`, {
        method: 'PATCH',
        body: JSON.stringify({ seatCount }),
      }),
    cancelOrganizationSubscription: (orgId: string, subscriptionId: string) =>
      fetchApi(`/admin/organizations/${orgId}/subscriptions/${subscriptionId}/cancel`, {
        method: 'POST',
      }),
    cancelOrganizationTrial: (
      orgId: string,
      subscriptionId: string,
      allowRetrial: boolean
    ) =>
      fetchApi(`/admin/organizations/${orgId}/subscriptions/${subscriptionId}/cancel-trial`, {
        method: 'POST',
        body: JSON.stringify({ allowRetrial }),
      }),
    deleteOrganizationCanceledSubscription: (orgId: string, subscriptionId: string) =>
      fetchApi(`/admin/organizations/${orgId}/subscriptions/${subscriptionId}`, {
        method: 'DELETE',
      }),

    inviteOrganizationUsers: (orgId: string, emails: string[]) =>
      fetchApi(`/admin/organizations/${orgId}/invitations`, {
        method: 'POST',
        body: JSON.stringify({ emails }),
      }),

    listOrganizationMembers: (orgId: string) =>
      fetchApi(`/admin/organizations/${orgId}/members`),

    addOrganizationMember: (orgId: string, email: string, role: string) =>
      fetchApi(`/admin/organizations/${orgId}/members`, {
        method: 'POST',
        body: JSON.stringify({ email, role }),
      }),

    updateOrganizationMemberRole: (orgId: string, userId: string, role: string) =>
      fetchApi(`/admin/organizations/${orgId}/members/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      }),

    removeOrganizationMember: (orgId: string, userId: string) =>
      fetchApi(`/admin/organizations/${orgId}/members/${userId}`, {
        method: 'DELETE',
      }),
    listOrganizationSeats: (orgId: string) =>
      fetchApi(`/admin/organizations/${orgId}/seats`),
    getOrganizationSeatAvailability: (orgId: string, appId: string) =>
      fetchApi(`/admin/organizations/${orgId}/seats/available/${appId}`),
    assignOrganizationSeat: (orgId: string, userId: string, appId: string) =>
      fetchApi(`/admin/organizations/${orgId}/seats`, {
        method: 'POST',
        body: JSON.stringify({ userId, appId }),
      }),
    unassignOrganizationSeat: (orgId: string, userId: string, appId: string) =>
      fetchApi(`/admin/organizations/${orgId}/seats/${userId}/${appId}`, {
        method: 'DELETE',
      }),
    listUsers: (options?: {
      search?: string;
      ignoreAnnea?: boolean;
      access?: 'all' | 'with-current-access' | 'without-current-access';
    }) => {
      const params = new URLSearchParams();
      if (options?.search) params.set('search', options.search);
      if (typeof options?.ignoreAnnea === 'boolean') {
        params.set('ignoreAnnea', String(options.ignoreAnnea));
      }
      if (options?.access && options.access !== 'all') {
        params.set('access', options.access);
      }
      const query = params.toString();
      return fetchApi(`/admin/users${query ? `?${query}` : ''}`);
    },
    createUser: (data: {
      email: string;
      name?: string;
      password: string;
      skipVerificationEmail?: boolean;
    }) =>
      fetchApi('/admin/users', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    getUser: (id: string) => fetchApi(`/admin/users/${id}`),
    deleteUser: (id: string) =>
      fetchApi(`/admin/users/${id}`, {
        method: 'DELETE',
      }),
    createUserSubscription: (
      userId: string,
      data: { appId: string; priceId?: string; trialDays?: number; trialOnly?: boolean; manual?: boolean; expiresAt?: string | null }
    ) =>
      fetchApi(`/admin/users/${userId}/subscriptions`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    updateManualAccessExpiry: (scope: 'users' | 'organizations', ownerId: string, subscriptionId: string, expiresAt: string | null) =>
      fetchApi(`/admin/${scope}/${ownerId}/subscriptions/${subscriptionId}/access`, { method: 'PATCH', body: JSON.stringify({ expiresAt }) }),
    cancelUserSubscription: (userId: string, subscriptionId: string) =>
      fetchApi(`/admin/users/${userId}/subscriptions/${subscriptionId}/cancel`, { method: 'POST' }),
    cancelUserTrial: (
      userId: string,
      subscriptionId: string,
      allowRetrial: boolean
    ) =>
      fetchApi(`/admin/users/${userId}/subscriptions/${subscriptionId}/cancel-trial`, {
        method: 'POST',
        body: JSON.stringify({ allowRetrial }),
      }),
    deleteUserCanceledTrial: (userId: string, subscriptionId: string) =>
      fetchApi(`/admin/users/${userId}/subscriptions/${subscriptionId}`, {
        method: 'DELETE',
      }),
    deleteUserTrialHistory: (userId: string, appId: string) =>
      fetchApi(`/admin/users/${userId}/trial-history/${appId}`, {
        method: 'DELETE',
      }),
  },

  analytics: {
    summary: (
      scope: 'global' | 'org' | 'user' = 'global',
      scopeId?: string,
      options?: { ignoreAnnea?: boolean }
    ) => {
      const params = new URLSearchParams({ scope });
      if (scope === 'org' && scopeId) params.set('orgId', scopeId);
      if (scope === 'user' && scopeId) params.set('userId', scopeId);
      if (typeof options?.ignoreAnnea === 'boolean') {
        params.set('ignoreAnnea', String(options.ignoreAnnea));
      }
      return fetchApi(`/analytics/summary?${params.toString()}`);
    },
    usersOrganizations: (options?: { ignoreAnnea?: boolean }) => {
      const params = new URLSearchParams();
      if (typeof options?.ignoreAnnea === 'boolean') {
        params.set('ignoreAnnea', String(options.ignoreAnnea));
      }
      const query = params.toString();
      return fetchApi(`/analytics/users-organizations${query ? `?${query}` : ''}`);
    },
    expiredAccessReport: (options?: {
      from?: string;
      to?: string;
      ignoreAnnea?: boolean;
    }) => {
      const params = new URLSearchParams();
      if (options?.from) params.set('from', options.from);
      if (options?.to) params.set('to', options.to);
      if (typeof options?.ignoreAnnea === 'boolean') {
        params.set('ignoreAnnea', String(options.ignoreAnnea));
      }
      const query = params.toString();
      return fetchApi(`/analytics/expired-access-report${query ? `?${query}` : ''}`);
    },
    events: (
      appSlug: string,
      options?: {
        days?: number;
        limit?: number;
        functionName?: string;
        eventName?: string;
        organizationId?: string;
        userId?: string;
        ignoreAnnea?: boolean;
      }
    ) => {
      const params = new URLSearchParams();
      if (options?.days) params.set('days', String(options.days));
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.functionName) params.set('functionName', options.functionName);
      if (options?.eventName) params.set('eventName', options.eventName);
      if (options?.organizationId) params.set('orgId', options.organizationId);
      if (options?.userId) params.set('userId', options.userId);
      if (typeof options?.ignoreAnnea === 'boolean') {
        params.set('ignoreAnnea', String(options.ignoreAnnea));
      }
      const query = params.toString();
      return fetchApi(`/analytics/apps/${appSlug}/events${query ? `?${query}` : ''}`);
    },
    listEvents: (
      options?: {
        appSlug?: string;
        days?: number;
        limit?: number;
        functionName?: string;
        eventName?: string;
        organizationId?: string;
        userId?: string;
        ignoreAnnea?: boolean;
      }
    ) => {
      const params = new URLSearchParams();
      if (options?.appSlug) params.set('appSlug', options.appSlug);
      if (options?.days) params.set('days', String(options.days));
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.functionName) params.set('functionName', options.functionName);
      if (options?.eventName) params.set('eventName', options.eventName);
      if (options?.organizationId) params.set('orgId', options.organizationId);
      if (options?.userId) params.set('userId', options.userId);
      if (typeof options?.ignoreAnnea === 'boolean') {
        params.set('ignoreAnnea', String(options.ignoreAnnea));
      }
      const query = params.toString();
      return fetchApi(`/analytics/events${query ? `?${query}` : ''}`);
    },
    users: () => fetchApi('/analytics/users'),
  },
};
